import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-[--green-base] text-[#050505] font-semibold hover:bg-[--green-highlight] shadow-[0_0_20px_-5px_rgba(117,254,184,0.45)]",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-border bg-transparent text-foreground hover:bg-secondary hover:border-foreground/30",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-secondary hover:text-foreground text-muted-foreground",
        link: "text-[--green-base] underline-offset-4 hover:underline",
        hero: "relative bg-[--green-base] text-[#050505] font-semibold tracking-tight hover:bg-[--green-highlight] transition-all shadow-[0_0_30px_-5px_rgba(117,254,184,0.5)] hover:shadow-[0_0_45px_-5px_rgba(143,255,200,0.6)]",
        glass: "bg-secondary/50 backdrop-blur-md border border-border text-foreground hover:bg-secondary/70",
        glow: "bg-[--green-base] text-[#050505] font-semibold hover:bg-[--green-highlight] shadow-[0_0_25px_-5px_rgba(117,254,184,0.5)] hover:shadow-[0_0_35px_-5px_rgba(143,255,200,0.6)]",
      },
      size: {
        default: "h-10 px-5 py-2",
        sm: "h-9 rounded-md px-4 text-sm",
        lg: "h-11 rounded-md px-6 text-sm",
        xl: "h-12 rounded-md px-8 text-base",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
